'use client';

import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { MarketCoin, TickerStream, PriceAlert } from '@glintfans/shared';

const WS_URL = process.env.NEXT_PUBLIC_WS_URL || '';

export interface TickerMap {
  [symbol: string]: TickerStream;
}

/**
 * Opens a single Socket.IO connection, subscribes to the `tickers` channel
 * (Binance !ticker@arr fanned out by the API gateway) and returns a live map
 * symbol -> latest stream snapshot, reconnecting automatically.
 */
export function useRealtimeTickers(): TickerMap {
  const [map, setMap] = useState<TickerMap>({});
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 15000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('subscribe', { channels: ['tickers'] });
    });
    socket.on('tickers', (raw: string) => {
      try {
        const msg = JSON.parse(raw) as { items?: TickerStream[] };
        const items = msg.items;
        if (!items || items.length === 0) return;
        setMap((prev) => {
          const next = { ...prev };
          for (const item of items) next[item.symbol] = item;
          return next;
        });
      } catch {
        /* ignore malformed */
      }
    });
    socket.on('disconnect', () => {
      /* socket.io auto-reconnects */
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  return map;
}

/**
 * Subscribes to the `alerts` channel and invokes the callback whenever a
 * price alert is triggered server-side (used to pop a system-style toast).
 */
export function useRealtimeAlerts(onAlert: (alert: PriceAlert) => void): void {
  const cbRef = useRef(onAlert);
  cbRef.current = onAlert;

  useEffect(() => {
    const socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 15000,
    });

    socket.on('connect', () => {
      socket.emit('subscribe', { channels: ['alerts'] });
    });
    socket.on('alerts', (raw: string) => {
      try {
        const msg = JSON.parse(raw) as { type?: string; alert?: PriceAlert };
        if (msg?.type === 'alert_triggered' && msg.alert) cbRef.current(msg.alert);
      } catch {
        /* ignore malformed */
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);
}

/** Merge live ticker fields into a REST-fetched coin row. */
export function mergeLive(coin: MarketCoin, live?: TickerStream): MarketCoin {
  // This stream contains Binance perpetual tickers only. Never overwrite another market.
  if (!live || coin.type === 'spot' || (coin.exchange && coin.exchange.toLowerCase() !== 'binance')) return coin;
  return {
    ...coin,
    price: live.price,
    priceChangePercent24h: live.priceChangePercent24h,
    high24h: live.high24h,
    low24h: live.low24h,
    quoteVolume24h: live.quoteVolume24h,
    updatedAt: live.updatedAt,
  };
}
